import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ApplicationRow {
  readonly applicationId: string;
  readonly applicationCode: string;
  readonly applicationName: string;
  readonly applicationType: string;
  readonly status: string;
  readonly currentVersionNo: number;
  readonly securityConfigured: boolean;
}

interface VersionRow {
  readonly versionId: string;
  readonly versionNo: number;
  readonly versionLabel: string | null;
  readonly status: string;
}

const APP_TYPES = ['', 'REQUEST_FORM', 'APPROVAL', 'SURVEY', 'INSPECTION', 'VENDOR', 'AUDIT', 'COMPLIANCE', 'CUSTOM'];
const STATUSES = ['', 'DRAFT', 'TESTING', 'PUBLISHED', 'ARCHIVED'];
const CREATE_TYPES = APP_TYPES.filter(Boolean);

/**
 * M28 — Application Builder (§6 / §19 / §24 / §32). The citizen-developer's home
 * for the no-code application portfolio: every application_master (T364) row
 * with its type, lifecycle status, current published version (§19) and whether
 * the mandatory security configuration (BR-05) has been completed. Supports the
 * full create → configure security → publish → archive journey plus §19
 * versioning (new version, publish version, rollback — BR-03/BR-07), each wired
 * to the guarded /v1/lcnc/applications write API. Filterable by type and status.
 */
export function M28ApplicationBuilderPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ApplicationRow[]>([]);
  const [applicationType, setApplicationType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // create form
  const [name, setName] = useState('');
  const [newType, setNewType] = useState('CUSTOM');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // version panel
  const [openVersionsFor, setOpenVersionsFor] = useState<string | null>(null);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [versionLabel, setVersionLabel] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (applicationType) params.set('applicationType', applicationType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: ApplicationRow[] }>(`/v1/lcnc/applications${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, applicationType, status, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m28.applicationBuilder.notAuthorised', { verb, perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!name.trim()) { setActionError(tr('m28.applicationBuilder.validationName')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: ApplicationRow }>(`/v1/lcnc/applications`, {
        method: 'POST',
        body: { applicationName: name.trim(), applicationType: newType, description: description.trim() || undefined },
        token,
      });
      setNotice(tr('m28.applicationBuilder.noticeCreated', { code: r.data.applicationCode }));
      setName('');
      setDescription('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m28.applicationBuilder.verbCreate'), 'APP_CREATE'));
    } finally {
      setBusy(false);
    }
  };

  const runRowAction = async (
    fn: () => Promise<unknown>,
    successMsg: string,
    verb: string,
    perm: string,
  ): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const configureSecurity = (a: ApplicationRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/applications/${a.applicationId}/security`, {
        method: 'POST', body: { securityJson: { configuredVia: 'admin-ui' } }, token,
      }),
      tr('m28.applicationBuilder.noticeSecurity', { code: a.applicationCode }), tr('m28.applicationBuilder.verbConfigureSecurity'), 'APP_UPDATE',
    );

  const publish = (a: ApplicationRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/applications/${a.applicationId}/publish`, { method: 'POST', body: {}, token }),
      tr('m28.applicationBuilder.noticePublished', { code: a.applicationCode }), tr('m28.applicationBuilder.verbPublish'), 'APP_PUBLISH',
    );

  const archive = (a: ApplicationRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/applications/${a.applicationId}/archive`, { method: 'POST', body: {}, token }),
      tr('m28.applicationBuilder.noticeArchived', { code: a.applicationCode }), tr('m28.applicationBuilder.verbArchive'), 'APP_ARCHIVE',
    );

  const loadVersions = async (a: ApplicationRow): Promise<void> => {
    if (openVersionsFor === a.applicationId) { setOpenVersionsFor(null); return; }
    setActionError(null);
    setOpenVersionsFor(a.applicationId);
    try {
      const r = await apiRequest<{ data: VersionRow[] }>(`/v1/lcnc/applications/${a.applicationId}/versions`, { token });
      setVersions(r.data);
    } catch (err) {
      setActionError(failMessage(err, tr('m28.applicationBuilder.verbViewVersions'), 'APP_VIEW'));
      setVersions([]);
    }
  };

  const createVersion = async (a: ApplicationRow): Promise<void> => {
    await runRowAction(
      () => apiRequest(`/v1/lcnc/applications/${a.applicationId}/versions`, {
        method: 'POST', body: { versionLabel: versionLabel.trim() || undefined }, token,
      }),
      tr('m28.applicationBuilder.noticeVersionCreated', { code: a.applicationCode }), tr('m28.applicationBuilder.verbCreateVersion'), 'APP_UPDATE',
    );
    setVersionLabel('');
    if (openVersionsFor === a.applicationId) {
      try {
        const r = await apiRequest<{ data: VersionRow[] }>(`/v1/lcnc/applications/${a.applicationId}/versions`, { token });
        setVersions(r.data);
      } catch { /* surfaced by runRowAction */ }
    }
  };

  const publishVersion = (a: ApplicationRow, v: VersionRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/applications/${a.applicationId}/versions/${v.versionId}/publish`, { method: 'POST', body: {}, token }),
      tr('m28.applicationBuilder.noticeVersionPublished', { version: v.versionNo, code: a.applicationCode }), tr('m28.applicationBuilder.verbPublishVersion'), 'APP_PUBLISH',
    );

  const rollbackVersion = (a: ApplicationRow, v: VersionRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/applications/${a.applicationId}/versions/${v.versionId}/rollback`, { method: 'POST', body: {}, token }),
      tr('m28.applicationBuilder.noticeRolledBack', { code: a.applicationCode, version: v.versionNo }), tr('m28.applicationBuilder.verbRollback'), 'ROLLBACK',
    );

  return (
    <main aria-label="application-builder">
      <h1>{tr('m28.applicationBuilder.title')}</h1>

      <section aria-label="create-application">
        <h2>{tr('m28.applicationBuilder.newHeading')}</h2>
        <form aria-label="application-form" onSubmit={(e) => void create(e)}>
          <label>{tr('m28.applicationBuilder.fieldName')}<input aria-label="field-application-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{tr('m28.applicationBuilder.fieldType')}
            <select aria-label="field-application-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>{tr('m28.applicationBuilder.fieldDescription')}<input aria-label="field-description" value={description} onChange={(e) => setDescription(e.target.value)} /></label>
          <button type="submit" aria-label="submit-application" disabled={busy}>{busy ? tr('m28.applicationBuilder.creating') : tr('m28.applicationBuilder.create')}</button>
        </form>
        {actionError && <p role="alert" aria-label="application-error">{actionError}</p>}
        {notice && <p aria-label="application-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m28.applicationBuilder.filterType')}
          <select aria-label="filter-application-type" value={applicationType} onChange={(e) => setApplicationType(e.target.value)}>
            {APP_TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m28.applicationBuilder.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m28.applicationBuilder.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m28.applicationBuilder.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m28.applicationBuilder.loading')}</p> : (
        <>
          <p aria-label="application-count">{tr('m28.applicationBuilder.count', { count: rows.length })}</p>
          <table aria-label="application-table">
            <thead><tr><th>{tr('m28.applicationBuilder.colCode')}</th><th>{tr('m28.applicationBuilder.colName')}</th><th>{tr('m28.applicationBuilder.colType')}</th><th>{tr('m28.applicationBuilder.colStatus')}</th><th>{tr('m28.applicationBuilder.colVersion')}</th><th>{tr('m28.applicationBuilder.colSecurity')}</th><th>{tr('m28.applicationBuilder.colActions')}</th></tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.applicationId}>
                  <td>{a.applicationCode}</td><td>{a.applicationName}</td><td>{a.applicationType}</td>
                  <td>{a.status}</td><td>v{a.currentVersionNo}</td>
                  <td>{a.securityConfigured ? tr('m28.applicationBuilder.configured') : tr('m28.applicationBuilder.pending')}</td>
                  <td>
                    <button type="button" aria-label={`security-${a.applicationCode}`} onClick={() => void configureSecurity(a)}>{tr('m28.applicationBuilder.btnConfigureSecurity')}</button>
                    <button type="button" aria-label={`publish-${a.applicationCode}`} onClick={() => void publish(a)}>{tr('m28.applicationBuilder.btnPublish')}</button>
                    <button type="button" aria-label={`archive-${a.applicationCode}`} onClick={() => void archive(a)}>{tr('m28.applicationBuilder.btnArchive')}</button>
                    <button type="button" aria-label={`versions-${a.applicationCode}`} onClick={() => void loadVersions(a)}>{tr('m28.applicationBuilder.btnVersions')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {openVersionsFor && (() => {
            const app = rows.find((r) => r.applicationId === openVersionsFor);
            if (!app) return null;
            return (
              <section aria-label="version-panel">
                <h2>{tr('m28.applicationBuilder.versionsHeading', { code: app.applicationCode })}</h2>
                <div>
                  <label>{tr('m28.applicationBuilder.fieldVersionLabel')}<input aria-label="field-version-label" value={versionLabel} onChange={(e) => setVersionLabel(e.target.value)} /></label>
                  <button type="button" aria-label="create-version" onClick={() => void createVersion(app)}>{tr('m28.applicationBuilder.btnNewVersion')}</button>
                </div>
                <table aria-label="version-table">
                  <thead><tr><th>{tr('m28.applicationBuilder.vColVersion')}</th><th>{tr('m28.applicationBuilder.vColLabel')}</th><th>{tr('m28.applicationBuilder.vColStatus')}</th><th>{tr('m28.applicationBuilder.vColActions')}</th></tr></thead>
                  <tbody>
                    {versions.length === 0 ? (
                      <tr><td colSpan={4}>{tr('m28.applicationBuilder.noVersions')}</td></tr>
                    ) : versions.map((v) => (
                      <tr key={v.versionId}>
                        <td>v{v.versionNo}</td><td>{v.versionLabel ?? '—'}</td><td>{v.status}</td>
                        <td>
                          <button type="button" aria-label={`publish-version-${v.versionNo}`} onClick={() => void publishVersion(app, v)}>{tr('m28.applicationBuilder.btnPublishVersion')}</button>
                          <button type="button" aria-label={`rollback-version-${v.versionNo}`} onClick={() => void rollbackVersion(app, v)}>{tr('m28.applicationBuilder.btnRollback')}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            );
          })()}
        </>
      )}
    </main>
  );
}
